import React from 'react';
import { get, forEach, cloneDeep, findIndex, filter, find, isUndefined, isEmpty, toNumber, merge } from 'lodash';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string';

// React view component to render
import { checkoutPrescriptionInfo as CheckoutPrescriptionInfoView } from 'ui/views/widgets/checkoutPrescriptionInfo';
import { CheckoutPrescriptionInfoService } from 'services/checkoutPrescriptionInfo';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import * as confirmOverlay from 'utils/confirmOverlay';
import { getLabel } from 'utils/getLabel';
import { getStore } from 'store';
import { REDUCER } from 'constants/entity-names';
import { getBackRouteForCheckoutResolver } from 'resolvers/getBackRouteForCheckout';

// get store
const store = getStore();

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { PrescriptionInfo } from 'ui/containers/PrescriptionInfo';
 *
 * // render
 * <PrescriptionInfo propName='propValue'/>
 */
class _CheckoutPrescriptionInfo extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        this.handleActionClick = this.handleActionClick.bind( this );
        this.cancelOrder = this.cancelOrder.bind( this );
        this.updateRxEstimatedCost = this.updateRxEstimatedCost.bind( this );

        const queryParams = queryString.parse( this.props.location.search );

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
            requestPayload: undefined,
            daysSupplyInfo: undefined,
            origin: get( queryParams, 'origin', 'memberManagement' )
        };

        // create service instance
        this.checkoutPrescriptionInfoService = new CheckoutPrescriptionInfoService();
        
        // start data fetch
        this.fetchComponentData();
    }

    /*********************************************/

    /**
     * @desc Fetch component response.
     */
    async fetchComponentData() {
        log.debug( 'PrescriptionInfo.fetchComponentData()' );

        // cancel ongoing request
        this.checkoutPrescriptionInfoService.cancel();
        
        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );
        const memberId = get( this.props.requestPayload, 'context.memberId', null );
        const patientId=  get( this.props.requestPayload, 'context.patientId', null );
        // fetch component data
        const { status, data } = await this.checkoutPrescriptionInfoService.fetchComponentData( {
            url: this.props.widget.url,
            data: {
                context: {
                    callId,
                    callType,
                    memberId,
                    patientId
                }
            }
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {

            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data,
                requestPayload: {
                    context: {
                        callId,
                        callType,
                        memberId,
                        patientId
                    }
                }
            }, () => {
                
                this.updateRxEstimatedCost();
            } );
        }
    }
    
    /**
     * @desc: action executed when user updates his drug selection
     */
    selectPrescriptionDrug( { rxId, drugId } ) {
        log.debug( 'PrescriptionInfo.selectPrescriptionDrug()' );

        // get rx list
        const componentData = cloneDeep( this.state.componentData );
        const prescriptions = get( componentData, 'prescriptions', [] );

        // find rx index
        const prescriptionIndex = findIndex( prescriptions, { id: rxId } );

        if( -1 === prescriptionIndex ) {
            return null;
        }

        const drugsList = prescriptions[ prescriptionIndex ].drugs;

        // get the drug list
        //let previousSelectedDays = null;
        const previousSelectedDrugIndex = findIndex( drugsList, { selected: true } );

        if( -1 !== previousSelectedDrugIndex ) {
            const selectedDaysOfSupplyIndex = findIndex( drugsList[ previousSelectedDrugIndex ].daysOfSupply, { selected: true } );
            if( -1 !== selectedDaysOfSupplyIndex ) {

                //previousSelectedDays = drugsList[ previousSelectedDrugIndex ].daysOfSupply[ selectedDaysOfSupplyIndex ].days;
                drugsList[ previousSelectedDrugIndex ].daysOfSupply[ selectedDaysOfSupplyIndex ].selected = false;
            }
        }
        
        forEach( drugsList, drug => {
            drug.selected = drug.id === drugId ? true: false;
            
            // if previousSelectedDays is not null, select the drug same value of drugs
            // if( ! isNil( previousSelectedDays )  ) {
            //     forEach( drug.daysOfSupply, drugSupply => {
            //         drugSupply.selected = drug.id === drugId && drugSupply.days === previousSelectedDays;
            //     } );
            // }
        } );


        // updaete state with the new selected drug
        componentData.prescriptions[ prescriptionIndex ].drugs = drugsList;
        this.setState( { componentData: componentData }, () => {

            this.updateRxEstimatedCost();
        } );
    }

    /**
     * @desc: action executed when user selects or deselects a prescription
     */
    handleRxSelection( { rxId } ) {
        log.debug( 'PrescriptionInfo.selectPrescriptionDrug()' );

        // get rx list
        const componentData = cloneDeep( this.state.componentData );

        const _prescriptions = get( componentData, 'prescriptions', [] );
        const rxIndex = findIndex( _prescriptions, { id: rxId } );

        if( _prescriptions[ rxIndex ].selected ) {
            _prescriptions[ rxIndex ].selected = false;

            const drugsList = _prescriptions[ rxIndex ].drugs;

            forEach( drugsList, drugs => {
                drugs.selected = false;

                forEach( drugs.daysOfSupply, days => {
                    days.selected = false;
                } );
            } );

        } else {
            _prescriptions[ rxIndex ].selected = true;
            _prescriptions[ rxIndex ].drugs[ 0 ].selected = true;
            _prescriptions[ rxIndex ].drugs[ 0 ].daysOfSupply[ 0 ].selected = true;
        }

        // updaete state with the new selected drug
        componentData.prescriptions = _prescriptions;
        this.setState( { componentData: componentData }, () => {
            this.updateRxEstimatedCost();

        } );
    }

    /**
     * calculate the new rx cost, every time user updates the selection
     */
    updateRxEstimatedCost() {
        log.debug( 'PrescriptionInfo.updateRxEstimatedCost()' );

        const componentData = cloneDeep( this.state.componentData );
        const _prescriptions = get( componentData, 'prescriptions', [] );

        let rxEstimatedCost = 0;
        forEach( _prescriptions, rx => {

            if( rx.selected ) {

                const selectedDrug = find( rx.drugs, { selected: true } );
                if( ! isUndefined( selectedDrug ) ) {
                    const selectedDaysOfSupply = find( selectedDrug.daysOfSupply, { selected: true  } );

                    if( ! isUndefined( selectedDaysOfSupply ) ) {
                        const selctedValue = ! isEmpty( selectedDaysOfSupply.value ) ? toNumber( selectedDaysOfSupply.value ): 0;
                        rxEstimatedCost = rxEstimatedCost + selctedValue;
                    }
                }
            }
        } );

        componentData.costCalculation.rxEstimatedCost = rxEstimatedCost;
        this.setState( { componentData: componentData } );

    }

    /**
     * @desc: action executed when user updates his drug days supply selection
     */
    handleDrugDaysSupplySelection( { item, rxId, drugId } ) {
        log.debug( 'PrescriptionInfo.handleDrugDaysSupplySelection()' );

        // get rx list
        const componentData = cloneDeep( this.state.componentData );
        const prescriptions = get( componentData, 'prescriptions', [] );

        // find rx and drug index
        const prescriptionIndex = findIndex( prescriptions, { id: rxId } );
        const drugIndex = findIndex( prescriptions[ prescriptionIndex ].drugs, { id: drugId } );

        // get the daysOfSupply list
        const daysOfSupply = prescriptions[ prescriptionIndex ].drugs[ drugIndex ].daysOfSupply;

        forEach( daysOfSupply, day => {
            day.selected = day.days === item.days ? true: false;
        } );

        // updaete state with the new selected drug
        componentData.prescriptions[ prescriptionIndex ].drugs[ drugIndex ].daysSupply = daysOfSupply;
        this.setState( { componentData: componentData }, () => {

            this.updateRxEstimatedCost();
        } );
    }

    /**
     * @desc: action executed when user clicks on the ne=xt button to moe forward in the checkout journey
     */
    async sumbitStepData( { url, data } ) {
        log.debug( 'PrescriptionInfo.sumbitStepData()' );

        // cancel ongoing request
        this.checkoutPrescriptionInfoService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status } = await this.checkoutPrescriptionInfoService.submitPrescriptionInfo( {
            url: url,
            data: merge( data, {
                ...this.state.requestPayload,
            } ),
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            // move to the next step
            this.props.moveForward();

        } else {
            errorOverlay.open(); // show error
        }
    }

      /**
     * @desc: cancel order
     */
    async cancelOrder( { action } ) {
        log.debug( 'PrescriptionInfo.cancelOrder()' );

        const shouldProceed = await confirmOverlay.open( {
			message: getLabel( action.confirmation ),
			iconName: 'close'
        } );
        
        if( shouldProceed ) {

            this.checkoutPrescriptionInfoService.cancel();

            // show page level preloader
            pageLevelPreloader.show();
    
            // get status and service data
            const { status } = await this.checkoutPrescriptionInfoService.cancelOrder( {
                url: action.url,
                data: {
                    ...this.state.requestPayload
                }
                
            } );
    
            // hide page level preloader
            pageLevelPreloader.hide();
    
            if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
    
                const queryParams = queryString.parse( this.props.location.search );
                const queryState = get( this.props.location, 'state', {} );

                const checkoutBackRoute = getBackRouteForCheckoutResolver( { queryParams, queryState } );
                this.props.history.push( checkoutBackRoute );
    
            } else {
                errorOverlay.open(); // show error
            }
        }
    }
 
    /**
     * @desc: action handler
     */
    async handleActionClick( { type, value, item, rx, drug } ) {
        log.debug( 'PrescriptionInfo.handleActionClick()' );

        if( 'navAction' === type && 'cancel' === value.name ) {
            this.cancelOrder( { action: value } );
        }

        if( 'navAction' === type && 'next' === value.name ) {

            const selectedRx = filter( this.state.componentData.prescriptions, rxdata => {
                return rxdata.selected;
            } );

            let prescriptions = [];

            forEach( selectedRx, rxdata => {

                const selectedDrug = find( rxdata.drugs, { selected: true } );
                const selectedDaysOfSupply = find( selectedDrug.daysOfSupply, { selected: true } );
                const prescriptionData = {};

                prescriptionData.id = rxdata.id;
                prescriptionData.drug = {
                    id: selectedDrug.id,
                    daysOfSupply: selectedDaysOfSupply.days,
                    value: selectedDaysOfSupply.value,
                    name: selectedDrug.name,
                    quantity: selectedDrug.quantity
                };
                prescriptions = [ ...prescriptions, prescriptionData ];
                
            } );

            this.sumbitStepData( {
                url: value.url,
                data: {
                    data: { prescriptions }
                }
            } );
        }

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );
        const memberId = get( this.props.requestPayload, 'context.memberId', undefined );
        const patientId = get( this.props.requestPayload, 'context.patientId', undefined );

        if( 'drugSelection' === type ) {
            this.selectPrescriptionDrug( {
                rxId: rx.id,
                drugId: drug.id
            } );
        }

        if( 'daysSupplySelection' === type && ( null === item.quantity ) && ( null === item.value ) ) {

            // show page level preloader
            pageLevelPreloader.show();

            // fetch component data
            const { status, data } = await this.checkoutPrescriptionInfoService.fetchDaysSupplyInfo( {
                url: this.state.componentData.daysSupplyInfo.url,
                data: {
                    context: {
                        callId,
                        callType,
                        memberId,
                        patientId
                    },
                    data: {
                        rxId: rx.id,
                        drugId: drug.id,
                        days: item.days
                    }
                }
            } );

            // hide page level preloader
            pageLevelPreloader.hide();

            // check if response status was success
            if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
                } );
            } else {
                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                    daysSupplyInfo: data
                } );
            }
            item.quantity = this.state.daysSupplyInfo.quantity;
            item.value = this.state.daysSupplyInfo.cost;
            this.handleDrugDaysSupplySelection( {
                item: item,
                rxId: rx.id,
                drugId: drug.id
            } );

        } else if ( 'daysSupplySelection' === type && !( null === item.quantity ) ) {
            this.handleDrugDaysSupplySelection( {
                item: item,
                rxId: rx.id,
                drugId: drug.id
            } );
        }

        if( 'rxSelection' === type ) {
            this.handleRxSelection( {
                rxId: rx.id
            } );
        }
    }

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.checkoutPrescriptionInfoService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <CheckoutPrescriptionInfoView
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                { ...get( this.state, 'componentData', {} ) }
                currentStep={ this.state.currentStep }
                onActionClick={ this.handleActionClick }
                history={ this.props.history }
            />
        );
    }
}

// set display name
_CheckoutPrescriptionInfo.displayName = 'PrescriptionInfo';

// set default props
_CheckoutPrescriptionInfo.defaultProps = {};

// prop types
_CheckoutPrescriptionInfo.propTypes = {};

export const CheckoutPrescriptionInfo = withRouter( _CheckoutPrescriptionInfo );
